import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FileText, MapPin, TrendingUp, DollarSign, Train } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { sampleReports } from "@/data/sampleReports";
import { usePageMeta } from "@/hooks/usePageMeta";
import { trackSampleReportView, trackButtonClick } from "@/services/analytics";

// City filter options
const cities = [
  { id: "all", label: "All Cities", emoji: "🇦🇺" },
  { id: "nsw", label: "Sydney", emoji: "🏙️" },
  { id: "vic", label: "Melbourne", emoji: "🎭" },
  { id: "qld", label: "Brisbane", emoji: "☀️" },
  { id: "wa", label: "Perth", emoji: "🌅" },
  { id: "sa", label: "Adelaide", emoji: "🍷" },
];

// Helper to extract city/state from address
const getStateFromAddress = (address: string): string => {
  if (address.includes("NSW")) return "nsw";
  if (address.includes("VIC")) return "vic";
  if (address.includes("QLD")) return "qld";
  if (address.includes("WA")) return "wa";
  if (address.includes("SA")) return "sa";
  if (address.includes("TAS")) return "tas";
  if (address.includes("NT")) return "nt";
  if (address.includes("ACT")) return "act";
  return "unknown";
};

// Helper to extract price from report
const extractPrice = (report: any): string | null => {
  if (report?.report?.estimated_value_range) {
    // Extract just the first value if it's a range like "$1.2M - $1.6M"
    const match = report.report.estimated_value_range.match(/\$[\d.]+[MKB]?/);
    return match ? match[0] : report.report.estimated_value_range;
  }
  return null;
};

// Helper to extract growth percentage
const extractGrowth = (report: any): string | null => {
  if (report?.report?.market_dynamics?.summary) {
    const match = report.report.market_dynamics.summary.match(/(\d+\.?\d*%)\s*(year|annual|y-o-y|year-on-year)/i);
    return match ? `+${match[1]}` : null;
  }
  return null;
};

// Helper to get transit info
const getTransitInfo = (report: any): string | null => {
  if (report?.quickFacts?.raw_data?.poi?.categories?.transport) {
    return `${report.quickFacts.raw_data.poi.categories.transport} stops`;
  }
  return null;
};

const SampleReports = () => {
  const navigate = useNavigate();
  const [selectedCity, setSelectedCity] = useState<string>("all");

  // Add page-specific meta tags and schema markup for SEO
  usePageMeta({
    title: "Sample Property Reports | Let's Be Real",
    description: "Explore real property insights across Sydney, Melbourne, Brisbane, Perth, and Adelaide. See how Let's Be Real uses Census 2021 data, real-time transit, and AI for honest property analysis nationwide.",
    canonical: "https://letsbereal.com.au/sample-reports",
    ogImage: "https://letsbereal.com.au/lbr.png",
    ogType: "website",
    twitterCard: "summary_large_image",
    keywords: "sample property reports, sydney property, melbourne property, brisbane property, perth property, adelaide property, australian property examples, property analysis samples, fitzroy, st kilda, west end, new farm, subiaco, north adelaide",
    schema: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Sample Property Reports",
      "description": "Collection of sample property reports demonstrating Let's Be Real's data-driven insights for Australian properties",
      "url": "https://letsbereal.com.au/sample-reports",
      "mainEntity": {
        "@type": "ItemList",
        "itemListElement": sampleReports.map((report, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "url": `https://letsbereal.com.au/sample-reports/${report.slug}`,
          "name": report.address,
          "description": report.shortDescription
        }))
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://letsbereal.com.au/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Sample Reports",
            "item": "https://letsbereal.com.au/sample-reports"
          }
        ]
      }
    }
  });

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-3 sm:gap-4 mb-4">
            <div className="p-2 sm:p-3 rounded-lg sm:rounded-xl bg-primary/10 flex-shrink-0">
              <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">Sample Property Reports</h1>
              <p className="text-xs sm:text-sm md:text-base text-muted-foreground mt-0.5 sm:mt-1">
                Explore real property insights generated from our platform
              </p>
            </div>
          </div>
        </div>

        {/* City Filter Pills */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2">
            {cities.map((city) => (
              <Button
                key={city.id}
                variant={selectedCity === city.id ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCity(city.id)}
                className={`transition-all duration-200 ${
                  selectedCity === city.id 
                    ? "shadow-md" 
                    : "hover:border-primary/50"
                }`}
              >
                <span className="mr-1.5">{city.emoji}</span>
                {city.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Sample Reports Grid */}
        <div className="space-y-4 sm:space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl sm:text-2xl font-bold">Explore Sample Insights</h2>
            <Badge variant="outline" className="text-xs">
              {sampleReports.filter(r => selectedCity === "all" || getStateFromAddress(r.address) === selectedCity).length} reports
            </Badge>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {sampleReports
              .filter((sample) => selectedCity === "all" || getStateFromAddress(sample.address) === selectedCity)
              .map((sample) => {
                const price = extractPrice(sample);
                const growth = extractGrowth(sample);
                const transit = getTransitInfo(sample);
                const stateCode = getStateFromAddress(sample.address);
                
                // State-specific accent colors
                const stateColors: Record<string, string> = {
                  nsw: "border-t-blue-500",
                  vic: "border-t-purple-500",
                  qld: "border-t-orange-500",
                  wa: "border-t-yellow-500",
                  sa: "border-t-red-500",
                  unknown: "border-t-primary",
                };
                
                return (
                  <Card 
                    key={sample.slug}
                    className={`group hover:shadow-xl hover:scale-[1.02] transition-all duration-300 cursor-pointer border-t-4 ${stateColors[stateCode] || stateColors.unknown}`}
                    onClick={() => {
                      trackSampleReportView(sample.shortTitle);
                      trackButtonClick('view_sample_report', 'sample_reports_page');
                      navigate(`/sample-reports/${sample.slug}`);
                    }}
                  >
                    <CardContent className="pt-5">
                      <div className="space-y-4">
                        {/* Quick stats badges */}
                        <div className="flex flex-wrap gap-2">
                          {price && (
                            <Badge variant="secondary" className="text-xs font-medium bg-green-500/10 text-green-700 border-green-200">
                              <DollarSign className="h-3 w-3 mr-0.5" />
                              {price}
                            </Badge>
                          )}
                          {growth && (
                            <Badge variant="secondary" className="text-xs font-medium bg-blue-500/10 text-blue-700 border-blue-200">
                              <TrendingUp className="h-3 w-3 mr-0.5" />
                              {growth}
                            </Badge>
                          )}
                          {transit && (
                            <Badge variant="secondary" className="text-xs font-medium bg-purple-500/10 text-purple-700 border-purple-200">
                              <Train className="h-3 w-3 mr-0.5" />
                              {transit}
                            </Badge>
                          )}
                        </div>

                        {/* Location Badge */}
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-primary" />
                          <span className="text-sm font-semibold text-primary">
                            {sample.shortTitle}
                          </span>
                        </div>

                        {/* Address */}
                        <h3 className="text-lg font-bold text-foreground leading-tight group-hover:text-primary transition-colors">
                          {sample.address}
                        </h3>

                        {/* Description */}
                        <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
                          {sample.shortDescription}
                        </p>

                        {/* CTA */}
                        <Button 
                          className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300"
                          variant="outline"
                        >
                          View Full Report →
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
          </div>
          
          {/* Empty state */}
          {sampleReports.filter(r => selectedCity === "all" || getStateFromAddress(r.address) === selectedCity).length === 0 && (
            <div className="text-center py-12 bg-muted/30 rounded-xl border border-dashed">
              <p className="text-muted-foreground">No sample reports available for this city yet.</p>
              <Button variant="link" onClick={() => setSelectedCity("all")}>
                View all reports →
              </Button>
            </div>
          )}
        </div>

        {/* Search Your Address CTA */}
        <Card className="mt-8 sm:mt-12 bg-gradient-to-r from-primary/10 to-primary/5">
          <CardContent className="pt-6 text-center px-4 sm:px-6">
            <h2 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-3">Want insights for your address?</h2>
            <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6">
              Get the same honest, data-driven analysis for any Australian property
            </p>
            <Button 
              size="lg" 
              onClick={() => {
                trackButtonClick('search_property', 'sample_reports_page');
                navigate("/");
              }}
              className="shadow-lg"
            >
              <MapPin className="h-4 w-4 mr-2" />
              Search Your Property
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SampleReports;

